import { useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './components/ui.tsx';

/** De quanto em quanto tempo a aba à vista confere a versão publicada. */
const CHECK_INTERVAL_MS = 2 * 60_000;

/**
 * A versão é a lista de arquivos para onde a página aponta: cada instalação gera nomes novos em
 * /assets. Em desenvolvimento esses arquivos não existem e a conferência fica desligada.
 */
export function assetsIn(html: string): string | null {
  const found = [...html.matchAll(/(?:src|href)="(\/assets\/[^"]+\.(?:js|css))"/g)];
  return found.length > 0
    ? found
        .map((match) => match[1]!)
        .sort()
        .join(' ')
    : null;
}

// Lido ao abrir o programa: depois, cada tela carregada acrescenta os próprios arquivos à página.
const loadedAssets = assetsIn(document.head.innerHTML);

/**
 * Lê a página publicada, sem depender de login. O navegador confere com o servidor (ETag): sem
 * mudança, a página nem é baixada de novo. Sem resposta útil (instalação em andamento, sem
 * internet), falha e a versão conhecida continua a mesma: nunca vira aviso.
 */
export async function fetchPublishedAssets(): Promise<string> {
  const response = await fetch('/', { cache: 'no-cache' });
  const assets = response.ok ? assetsIn(await response.text()) : null;
  if (!assets) throw new Error('published version unavailable');
  return assets;
}

/** Só há versão nova quando as duas são conhecidas e diferentes. */
export function hasNewVersion(loaded: string | null, published: string | undefined): boolean {
  return loaded !== null && published !== undefined && published !== loaded;
}

/**
 * A faixa não tira o foco nem recarrega sozinha: quem está digitando continua de onde parou. Fica
 * acima do botão Reportar e abaixo das janelas (z-50), porque atualizar apagaria o que há nelas.
 */
export function NewVersionBanner() {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      className="fixed right-4 bottom-20 left-4 z-30 flex flex-col gap-3 rounded-lg border border-rule bg-brand-soft p-4 text-sm text-ink shadow-[0_12px_32px_rgb(23_32_43/0.16)] sm:flex-row sm:items-center md:right-6 md:bottom-24 md:left-auto md:max-w-md lg:max-w-lg print:hidden"
    >
      <RefreshCw
        size={18}
        strokeWidth={1.75}
        aria-hidden
        className="hidden shrink-0 text-brand-text sm:block"
      />
      <p className="flex-1">
        <span className="block font-medium">{t('newVersion.title')}</span>
        <span>{t('newVersion.body')}</span>
      </p>
      <Button className="shrink-0" onClick={() => window.location.reload()}>
        {t('newVersion.action')}
      </Button>
    </div>
  );
}

/**
 * Avisa quem está com a aba aberta durante uma instalação (aba antiga segue rodando o código antigo).
 * Confere com a aba à vista, ao voltar para ela e quando um arquivo de tela some do servidor.
 */
export function NewVersionNotice() {
  const { data: publishedAssets, refetch } = useQuery({
    queryKey: ['published-version'],
    queryFn: fetchPublishedAssets,
    enabled: loadedAssets !== null,
    refetchInterval: CHECK_INTERVAL_MS,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
    retry: false,
  });
  useEffect(() => {
    const check = () => void refetch();
    window.addEventListener('vite:preloadError', check);
    return () => window.removeEventListener('vite:preloadError', check);
  }, [refetch]);
  if (!hasNewVersion(loadedAssets, publishedAssets)) return null;
  return <NewVersionBanner />;
}
